import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSocieta } from '../../context/SocietaContext';
import { usePermesso } from '../../context/PermessiContext';
import { apiFetch } from '../../lib/api';
import { getRuoloLabel } from '../../utils/ruoliLabel';
import { useChatAssistenza } from '../../context/ChatAssistenzaContext';
import { trovaVoceMenuPerPath } from '../../config/menu';
import { leggiVersione } from '../../utils/versione';

const VERSIONE = leggiVersione();

export default function Topbar() {
  const { societaAttiva, listaSocieta, isSocietaFissa, richiediCambioSocieta } = useSocieta();
  const { utente, can } = usePermesso();
  const isSuperadmin = utente?.ruolo === 'SUPERADMIN';
  const navigate = useNavigate();
  const location = useLocation();
  const { apri: apriAssistente } = useChatAssistenza();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [nonLetti, setNonLetti] = useState(0);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);


  const fetchNonLetti = useCallback(async () => {
    if (!can('aziendale.messaggi_lettura', 'view')) return;
    // Passa societaId se disponibile (utenti non-SUPERADMIN devono sempre filtrare)
    const params = societaAttiva ? `?societaId=${societaAttiva.id}` : '';
    try {
      const data = await apiFetch<{ count: number }>(`/api/messaggi/ricevuti/count-non-letti${params}`);
      setNonLetti(data.count ?? 0);
    } catch (_) {}
  }, [can, societaAttiva]);

  useEffect(() => {
    fetchNonLetti();
    const interval = setInterval(fetchNonLetti, 60_000);
    return () => clearInterval(interval);
  }, [fetchNonLetti]);

  const handleLogout = async () => {
    try { await apiFetch('/auth/logout', { method: 'POST' }); } catch (_) {}
    localStorage.removeItem('token');
    sessionStorage.removeItem('bop_login_needs_confirm');
    window.location.replace('/');
  };

  const initial = utente?.nome.charAt(0).toUpperCase() ?? '?';

  // Chat con l'assistente: parte sapendo in quale pagina si trova l'utente
  const apriChatAssistenza = () => {
    const voce = trovaVoceMenuPerPath(location.pathname);
    apriAssistente({
      urlPagina: window.location.href,
      modulo: voce?.modulo,
      moduloLabel: voce?.label,
    });
  };

  return (
    <header className="topbar">

      {/* ── Logo ──────────────────────────────────────────────────────────── */}
      <div className="topbar-logo">
        <img
          src={`${process.env.PUBLIC_URL}/bop-logo-reverse.svg`}
          alt="BOP"
          className="topbar-logo-img"
        />
        {/* Versione in esercizio: commit e data/ora della build */}
        <span className="bop-topbar-versione" title={VERSIONE.dettaglio}>
          <span className="bop-topbar-versione__numero">v {VERSIONE.versione}</span>
          {VERSIONE.rilascio && <span className="bop-topbar-versione__data">{VERSIONE.rilascio}</span>}
        </span>
      </div>

      {/* ── Selezione Società ────────────────────────────────────────────── */}
      <div className="topbar-center">
        {listaSocieta.length > 0 && (
          isSocietaFissa ? (
            <span className="topbar-societa-badge">{societaAttiva?.nome}</span>
          ) : (
            <select
              className="topbar-societa-select"
              value={societaAttiva ? String(societaAttiva.id) : ''}
              onChange={e => {
                const v = e.target.value;
                const target = v === '' ? null : (listaSocieta.find(s => String(s.id) === v) ?? null);
                richiediCambioSocieta(target);
              }}
            >
              {isSuperadmin && <option value="">Tutte le società</option>}
              {listaSocieta.map(s => (
                <option key={s.id} value={s.id}>{s.nome}</option>
              ))}
            </select>
          )
        )}
      </div>

      {/* ── Azioni destra ────────────────────────────────────────────────── */}
      <div className="topbar-actions">

        {/* Assistente AI (chat prima del ticket): pulsante colorato con etichetta, riconoscibile a colpo d'occhio */}
        {can('assistenza.richieste', 'edit') && (
          <button
            className="topbar-icon-btn topbar-assistente-btn"
            aria-label="Assistente AI"
            title="Chiedi aiuto all'assistente AI: risponde subito o apre un ticket"
            onClick={apriChatAssistenza}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 12a8 8 0 0 1-11.6 7.1L4 21l1.9-5.4A8 8 0 1 1 21 12z" />
              <path d="M12 8l.9 2.1 2.1.9-2.1.9L12 14l-.9-2.1L9 11l2.1-.9z" fill="currentColor" stroke="none" />
            </svg>
            <span className="topbar-assistente-btn__label">Assistente</span>
          </button>
        )}

        {/* Messaggi */}
        {can('aziendale.messaggi_lettura', 'view') && (
          <button
            className="topbar-icon-btn topbar-bell-btn"
            aria-label="Messaggi"
            onClick={() => navigate('/aziendale/messaggi')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="4" width="20" height="16" rx="2" />
              <polyline points="2,4 12,13 22,4" />
            </svg>
            {nonLetti > 0 && (
              <span className="topbar-bell-badge">{nonLetti > 99 ? '99+' : nonLetti}</span>
            )}
          </button>
        )}

        {/* Notifiche generiche */}
        <button
          className="topbar-icon-btn topbar-bell-btn"
          aria-label="Notifiche"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
        </button>

        {/* User menu */}
        <div ref={menuRef} className="topbar-user-wrap">
          <button className="topbar-user-btn" onClick={() => setUserMenuOpen(o => !o)}>
            <div className="topbar-user-avatar">{initial}</div>
            <div className="topbar-user-info">
              <span className="topbar-user-name">{utente?.nome}</span>
              <span className="topbar-user-role">{getRuoloLabel(utente?.ruolo ?? '')}</span>
            </div>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="topbar-user-chevron">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          {userMenuOpen && (
            <div className="topbar-dropdown">
              <div className="topbar-dropdown-header">
                <div className="topbar-dropdown-avatar">{initial}</div>
                <div>
                  <div className="topbar-dropdown-name">{utente?.nome}</div>
                  <div className="topbar-dropdown-role">{getRuoloLabel(utente?.ruolo ?? '')}</div>
                </div>
              </div>
              <div className="topbar-dropdown-divider" />
              <button className="topbar-dropdown-item topbar-dropdown-logout" onClick={handleLogout}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                Esci dall'applicazione
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
